/**
 * Donations API client — Fundraising Suite (epic TRUS-326).
 *
 * Wraps /api/donations — in-platform gifts (online card + offline cash/check).
 * All calls go through authFetch (Authorization + X-CBO-ID). For the PUBLIC
 * donor checkout use the unauthenticated /api/public/donations/* path instead.
 *
 * Backend: app/routers/donations.py.
 */

import { authFetch, authFetchJson } from '@/lib/authFetch';

export type DonationStatus = 'pending' | 'succeeded' | 'failed' | 'refunded' | 'partially_refunded';
export type DonationMethod = 'card' | 'cash' | 'check' | 'offline' | 'other';
export type ReceiptState = 'none' | 'issued' | 'sent' | 'failed';
export type ReceiptChannel = 'email' | 'mail' | 'in_person';

export interface Donation {
  id: number;
  cbo_id: number;
  donor_member_id?: number | null;
  donor_name?: string | null;
  donor_email?: string | null;
  amount: number;
  currency: string;
  platform_fee: number;
  net_amount?: number | null;
  campaign?: string | null;
  fund_key?: string | null;
  grant_obligation_id?: number | null;
  method: DonationMethod;
  status: DonationStatus;
  stripe_payment_intent_id?: string | null;
  is_tax_deductible: boolean;
  deductible_amount?: number | null;
  receipt_number?: string | null;
  receipt_url?: string | null;
  receipt_sent_at?: string | null;
  receipt_channel?: string | null;
  receipt_issued_at?: string | null;
  receipt_send_count?: number | null;
  receipt_last_error?: string | null;
  /** none | issued | sent | failed — one field instead of inferring from three. */
  receipt_state?: ReceiptState;
  event_id?: number | null;
  event_title?: string | null;
  donor_display_name?: string | null;
  payment_type?: string | null;
  ledger_posted: boolean;
  notes?: string | null;
  created_at: string;
  updated_at: string;
}

export interface OfflineDonationCreate {
  amount: number;
  method?: DonationMethod; // cash | check | offline | other
  donor_name?: string;
  donor_email?: string;
  donor_member_id?: number;
  campaign?: string;
  fund_key?: string;
  grant_obligation_id?: number;
  is_tax_deductible?: boolean;
  notes?: string;
  send_receipt?: boolean;
  event_id?: number; // tag the gift to a donor event so raised-at-event totals roll up
}

export interface DonationIntentCreate {
  amount: number;
  currency?: string;
  donor_name?: string;
  donor_email?: string;
  donor_member_id?: number;
  campaign?: string;
  fund_key?: string;
  grant_obligation_id?: number;
  is_tax_deductible?: boolean;
}

export interface DonationIntentResponse {
  donation_id: number;
  client_secret: string;
  payment_intent_id: string;
  amount: number;
  platform_fee: number;
  currency: string;
  mock: boolean;
}

function buildQuery(params: Record<string, any> = {}): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

export interface DonationFilters {
  donor_member_id?: number;
  status?: string;
  /** Gifts raised at a donor event — the tag was written but never returned. */
  event_id?: number;
  method?: DonationMethod;
  campaign?: string;
  receipt_status?: ReceiptState;
  year?: number;
  search?: string;
  limit?: number;
}

export async function listDonations(params: DonationFilters = {}): Promise<Donation[]> {
  return authFetchJson<Donation[]>(`/api/donations${buildQuery(params)}`);
}

export async function getDonation(id: number): Promise<Donation> {
  return authFetchJson<Donation>(`/api/donations/${id}`);
}

export async function createOfflineDonation(payload: OfflineDonationCreate): Promise<Donation> {
  return authFetchJson<Donation>('/api/donations/offline', { method: 'POST', jsonBody: payload });
}

export async function createDonationIntent(payload: DonationIntentCreate): Promise<DonationIntentResponse> {
  return authFetchJson<DonationIntentResponse>('/api/donations/intent', { method: 'POST', jsonBody: payload });
}

export interface TerminalStatus {
  enabled: boolean; // Stripe is configured on the platform
  connected_account: boolean; // this CBO has a connected Stripe account
  ready: boolean; // card-present is usable
}

export async function getTerminalStatus(): Promise<TerminalStatus> {
  return authFetchJson<TerminalStatus>('/api/donations/terminal/status');
}

export async function getTerminalConnectionToken(): Promise<{ secret: string }> {
  return authFetchJson<{ secret: string }>('/api/donations/terminal/connection-token', { method: 'POST' });
}

export interface TerminalPaymentIntentResponse {
  donation_id: number;
  client_secret: string;
  payment_intent_id: string;
  amount: number;
  platform_fee: number;
}

export async function createTerminalPaymentIntent(payload: {
  amount: number;
  event_id?: number;
  donor_name?: string;
  donor_email?: string;
  donor_member_id?: number;
  campaign?: string;
}): Promise<TerminalPaymentIntentResponse> {
  return authFetchJson<TerminalPaymentIntentResponse>('/api/donations/terminal/payment-intent', {
    method: 'POST',
    jsonBody: payload,
  });
}

export async function captureTerminalPayment(
  paymentIntentId: string,
): Promise<{ payment_intent_id: string; status: string }> {
  return authFetchJson('/api/donations/terminal/capture', {
    method: 'POST',
    jsonBody: { payment_intent_id: paymentIntentId },
  });
}


// ---------------------------------------------------------------------------
// Receipts (Phase 5). The receipt PDF is rendered on demand from the gift rather
// than stored, so it can always be re-opened — previously it existed only inside
// the email that was sent.
// ---------------------------------------------------------------------------

export interface ReceiptIssueResult {
  sent: boolean;
  issued: boolean;
  receipt_number?: string;
  receipt_url?: string;
  channel?: string | null;
  error?: string | null;
  reason?: string;
}

/**
 * Open a PDF that requires auth. The receipt/statement endpoints are behind
 * Authorization + X-CBO-ID, so a bare <a href> would 401 — fetch it as an auth'd
 * blob and open an object URL (the same approach client invoices use).
 */
async function openAuthedPdf(path: string, failure: string): Promise<void> {
  const response = await authFetch(path);
  if (!response.ok) {
    const err = await response.json().catch(() => ({ detail: failure }));
    throw new Error(err.detail || `HTTP ${response.status}`);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener,noreferrer');
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

/** Open the 501(c)(3) receipt PDF for a gift. */
export async function openReceiptPdf(donationId: number): Promise<void> {
  return openAuthedPdf(`/api/donations/${donationId}/receipt.pdf`, 'Could not open the receipt');
}

/** Open a donor's year-end giving statement for a tax year. */
export async function openAnnualStatement(memberId: number, year: number): Promise<void> {
  return openAuthedPdf(
    `/api/donations/statement/${memberId}?year=${year}`,
    'Could not open the giving statement',
  );
}

export async function issueReceipt(
  donationId: number,
  payload: { channel?: ReceiptChannel; to_email?: string } = {},
): Promise<ReceiptIssueResult> {
  return authFetchJson<ReceiptIssueResult>(`/api/donations/${donationId}/receipt`, {
    method: 'POST',
    jsonBody: payload,
  });
}

export async function bulkIssueReceipts(payload: {
  donation_ids?: number[];
  year?: number;
  event_id?: number;
  only_unreceipted?: boolean;
}): Promise<{ considered: number; sent: number; issued_not_sent: number; failed: number; detail?: string }> {
  return authFetchJson('/api/donations/receipts/bulk-issue', { method: 'POST', jsonBody: payload });
}

export interface ReceiptQueue {
  by_state: Record<string, { count: number; amount: number }>;
  unreceipted: number;
  issued_not_sent: number;
  failed: number;
  sent: number;
  blocked_no_email: number;
  pending: Donation[];
}

export async function getReceiptQueue(limit = 200): Promise<ReceiptQueue> {
  return authFetchJson<ReceiptQueue>(`/api/donations/receipts/queue?limit=${limit}`);
}
